import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_URL from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiSend, 
  FiPaperclip, 
  FiAtSign,
  FiX,
  FiCheck,
  FiCheckCircle,
  FiUser,
  FiClock,
  FiMessageSquare
} from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';

const ChatWidget = ({ onClose, isOpen }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [users, setUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchMessages();
      fetchOnlineUsers();
      fetchAllUsers();
      
      // Actualizar cada 30 segundos
      const interval = setInterval(() => {
        fetchMessages(true);
        fetchOnlineUsers();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/chat/messages?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (!silent) toast.error('Error al cargar mensajes');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/chat/online`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnlineUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Usar el mismo endpoint de usuarios asignables
      const response = await axios.get(`${API_URL}/api/users/assignable`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users for mentions:', error);
      // No mostrar toast para no molestar
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const token = localStorage.getItem('token');
      
      // Detectar menciones (@usuario)
      const mentionRegex = /@(\w+)/g;
      const mentions = [];
      let match;
      while ((match = mentionRegex.exec(newMessage)) !== null) {
        const mentionedUser = users.find(u => 
          u.name.toLowerCase().includes(match[1].toLowerCase())
        );
        if (mentionedUser) {
          mentions.push(mentionedUser._id);
        }
      }

      const response = await axios.post(
        `${API_URL}/api/chat/messages`,
        {
          message: newMessage,
          mentions
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(prev => [...prev, response.data.data]);
      setNewMessage('');
      setShowMentions(false);
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar mensaje');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Detectar si está escribiendo una mención
    const lastAtPos = value.lastIndexOf('@');
    if (lastAtPos !== -1 && (lastAtPos === 0 || value[lastAtPos - 1] === ' ')) {
      const searchTerm = value.slice(lastAtPos + 1).split(' ')[0];
      setMentionSearch(searchTerm);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (mentionedUser) => {
    const lastAtPos = newMessage.lastIndexOf('@');
    const beforeMention = newMessage.slice(0, lastAtPos);
    const afterMention = newMessage.slice(lastAtPos).split(' ').slice(1).join(' ');
    setNewMessage(`${beforeMention}@${mentionedUser.name} ${afterMention}`);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const filteredUsersForMention = users.filter(u => 
    u._id !== user?._id && 
    u.name.toLowerCase().includes(mentionSearch.toLowerCase())
  ).slice(0, 5);

  const getMessageStatus = (message) => {
    if (message.userId?._id === user?._id) {
      const readByOthers = message.readBy?.filter(
        r => r.userId !== user?._id
      ).length || 0;
      
      if (readByOthers > 0) {
        return <FiCheckCircle className="text-blue-600" size={14} />;
      }
      return <FiCheck className="text-gray-400" size={14} />;
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[600px] bg-white rounded-lg shadow-2xl flex flex-col z-50 border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b flex justify-between items-center bg-blue-600 text-white rounded-t-lg">
        <div className="flex items-center gap-2">
          <FiMessageSquare size={20} />
          <h3 className="font-semibold">Chat de Coordinación</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-green-400 text-white px-2 py-1 rounded-full">
            {onlineUsers.length} en línea
          </span>
          <button onClick={onClose} className="hover:bg-blue-700 p-1 rounded">
            <FiX size={20} />
          </button>
        </div>
      </div>

      {/* Online Users */}
      <div className="px-4 py-2 bg-gray-50 border-b text-sm flex gap-2 overflow-x-auto">
        {onlineUsers.map(ou => (
          <div key={ou._id} className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs whitespace-nowrap">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            {ou.name.split(' ')[0]}
            {ou.role === 'owner' && ' 👑'}
          </div>
        ))}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div
              key={msg._id}
              className={`flex ${msg.userId?._id === user?._id ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  msg.type === 'system'
                    ? 'bg-gray-100 text-gray-600 italic text-sm w-full text-center'
                    : msg.userId?._id === user?._id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {msg.type !== 'system' && msg.userId && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">
                      {msg.userId.name}
                      {msg.userId.role === 'owner' && ' 👑'}
                      {msg.userId.role === 'admin' && ` (Admin ${msg.userId.adminId})`}
                    </span>
                    {onlineUsers.some(ou => ou._id === msg.userId._id) && (
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    )}
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap break-words">
                  {msg.message}
                </p>
                <div className={`flex items-center justify-end gap-1 text-xs mt-1 ${
                  msg.userId?._id === user?._id ? 'text-blue-200' : 'text-gray-500'
                }`}>
                  <span>
                    {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true, locale: es })}
                  </span>
                  {getMessageStatus(msg)}
                </div>
                {msg.mentions?.length > 0 && (
                  <div className="text-xs mt-1 opacity-75">
                    Mencionó: {msg.mentions.map(m => m.name).join(', ')}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Mentions Dropdown */}
      {showMentions && filteredUsersForMention.length > 0 && (
        <div className="absolute bottom-20 left-4 right-4 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
          {filteredUsersForMention.map(u => (
            <button
              key={u._id}
              onClick={() => insertMention(u)}
              className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
            >
              <FiAtSign className="text-gray-400" size={14} />
              <span className="font-medium">{u.name}</span>
              {u.role === 'owner' && <span className="text-xs text-purple-600">(Owner)</span>}
              {u.role === 'admin' && <span className="text-xs text-blue-600">(Admin {u.adminId})</span>}
              {onlineUsers.some(ou => ou._id === u._id) && (
                <span className="w-2 h-2 bg-green-500 rounded-full ml-auto"></span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
            <FiPaperclip size={20} />
          </button>
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Escribe un mensaje... (@ para mencionar)"
            className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim()}
            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSend size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWidget;